import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Upload, Image as ImageIcon, X, Loader2, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import imageCompression from 'browser-image-compression';

interface Midia {
  id: string;
  nome: string;
  arquivo_url: string;
  caminho: string;
  alt: string;
  largura: number;
  altura: number;
  tamanho_bytes: number;
  tipo: string;
  tags: string[];
}

interface SeletorDeImagemProps {
  onSelect: (midia: Midia) => void;
  trigger?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SeletorDeImagem({ onSelect, trigger, isOpen, onOpenChange }: SeletorDeImagemProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const isControlled = isOpen !== undefined;
  const currentOpen = isControlled ? isOpen : open;
  const setCurrentOpen = isControlled ? onOpenChange! : setOpen;

  const { data: midias, isLoading } = useQuery({
    queryKey: ['admin-midias', searchTerm],
    queryFn: async () => {
      let query = supabase.from('midias').select('*').order('created_at', { ascending: false });
      if (searchTerm) {
        query = query.ilike('nome', `%${searchTerm}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as Midia[];
    },
    enabled: currentOpen
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploading(true);
      try {
        // Validation: size < 5MB
        if (file.size > 5 * 1024 * 1024) {
          throw new Error("Arquivo muito grande. Limite de 5MB.");
        }

        // Optimization
        const options = {
          maxWidthOrHeight: 1600,
          useWebWorker: true,
          fileType: 'image/webp'
        };
        const compressedFile = await imageCompression(file, options);
        
        const fileName = `${Date.now()}-${file.name.replace(/\.[^/.]+$/, "")}.webp`;
        const filePath = `blog/${fileName}`;

        // Upload to Storage
        const { error: uploadError } = await supabase.storage
          .from('midias')
          .upload(filePath, compressedFile);

        if (uploadError) throw uploadError;

        // Get URL
        const { data: { publicUrl } } = supabase.storage
          .from('midias')
          .getPublicUrl(filePath);

        // Save to Database
        const img = new Image();
        img.src = URL.createObjectURL(compressedFile);
        await new Promise((resolve) => {
          img.onload = resolve;
        });

        const { data: midiaData, error: dbError } = await supabase
          .from('midias')
          .insert([{
            nome: file.name,
            arquivo_url: publicUrl,
            caminho: filePath,
            alt: file.name.split('.')[0] || null,
            largura: img.width || null,
            altura: img.height || null,
            tamanho_bytes: compressedFile.size || null,
            tipo: 'image/webp'
          }])
          .select()
          .single();

        if (dbError) throw dbError;
        return midiaData as Midia;
      } finally {
        setUploading(false);
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-midias'] });
      toast.success("Imagem enviada com sucesso!");
      onSelect(data);
      setCurrentOpen(false);
    },
    onError: (error: any) => {
      toast.error(`Erro no upload: ${error.message}`);
    }
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadMutation.mutate(e.target.files[0]);
    }
  };

  const handleSelect = () => {
    const selected = midias?.find(m => m.id === selectedId);
    if (selected) {
      onSelect(selected);
      setCurrentOpen(false);
    }
  };

  return (
    <Dialog open={currentOpen} onOpenChange={setCurrentOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>Selecionar Imagem</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="biblioteca" className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 border-b">
            <TabsList className="w-full justify-start bg-transparent h-12 gap-6 p-0">
              <TabsTrigger 
                value="biblioteca" 
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-full px-0"
              >
                Biblioteca
              </TabsTrigger>
              <TabsTrigger 
                value="upload"
                className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-full px-0"
              >
                Enviar nova
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="biblioteca" className="flex-1 overflow-hidden flex flex-col p-0 m-0">
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Buscar na biblioteca..." 
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {midias?.map((midia) => (
                    <div 
                      key={midia.id}
                      onClick={() => setSelectedId(midia.id)}
                      className={`
                        relative aspect-square rounded-xl border-2 cursor-pointer group overflow-hidden
                        ${selectedId === midia.id ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-gray-200'}
                      `}
                    >
                      <img 
                        src={midia.arquivo_url} 
                        alt={midia.alt} 
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      {selectedId === midia.id && (
                        <div className="absolute top-2 right-2 bg-primary text-[#0C1A12] rounded-full p-1">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  ))}
                  {midias?.length === 0 && (
                    <div className="col-span-full py-20 text-center text-gray-500">
                      Nenhuma imagem encontrada.
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="p-4 border-t gap-2 bg-gray-50/50">
              <Button variant="outline" onClick={() => setCurrentOpen(false)}>Cancelar</Button>
              <Button 
                onClick={handleSelect} 
                disabled={!selectedId}
                className="bg-primary text-[#0C1A12] font-bold"
              >
                Usar esta imagem
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="upload" className="flex-1 flex flex-col items-center justify-center p-8 m-0">
            <div className="w-full max-w-md p-12 border-2 border-dashed rounded-3xl flex flex-col items-center gap-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative">
              <input 
                type="file" 
                className="absolute inset-0 opacity-0 cursor-pointer" 
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <Upload className="w-8 h-8" />}
              </div>
              <div className="text-center">
                <p className="font-bold text-[#0C1A12]">
                  {uploading ? 'Processando e enviando...' : 'Arraste uma imagem ou clique para selecionar'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  JPG, PNG ou WebP. Máximo 5MB.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
